/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useId, useMemo } from 'react';
import type { Feed, FeedEntry, UIComponent } from '~/services/layout/index';
import { twMerge } from 'tailwind-merge';
import { MaybeLink } from '~/components/maybe-link';
import { ResponsiveImage } from './responsive-image';
import { ImageOverlay } from './image-overlay';
import logger from '~/services/logger';
import { RuntimeV2 } from './runtime-v2';
import {
  type TapCellAnalyticsData,
  useTapCellAnalyticsData,
} from '~/hooks/use-tap-cell-analytics-data';
import { ProgressBarV2 } from './progress-bar-v2';
import { ActionIconV2 } from './action-icon-v2';
import {
  getCellClasses,
  getCellTextLabelClasses,
  getCellTextLabelsV2,
  getCellEntryImgSources,
  getCellBreakpointValues,
  getCellContainerCellTypeClasses,
  getCellImageCellTypeClasses,
  getCell3TextLabelContainerClasses,
  getCellHiddenClasses,
  getCellImagePlaceholderColorClasses,
  getCellImageAspectRatioClasses,
  cellTextLabels,
  getResponsiveCellValues,
  getPointerEventsClasses,
  type CellTextLabelsV2,
  type ResponsiveSources,
  getCellImageContainerClasses,
  getCell3IgnorePaddingClasses,
  getCellImageHiddenClasses,
  shouldRenderPowerCellImage,
} from './power-cell-getters';
import { useLoaderData } from '@remix-run/react';
import type { loader as rootLoader } from '~/routes/_index.ts';
import { getResponsiveCellConfigs } from './get-responsive-cell-configs';
import {
  getContinueWatchingProgress,
  isEmptyArray,
  seoSelectedBreakpoint,
} from '~/services/layout/layout';
import { LockBadge } from './lock-badge';
import { LabelWrapper } from './label-wrapper';
import { useTrackAnalyticsData } from '~/hooks/use-track-analytics-data';
import { PowerCellBorder } from './power-cell-border';
import {
  useResponsiveBreakpoint,
  type UseResponsiveBreakpoint,
} from '~/hooks/use-responsive-breakpoint';
import { InfoCellV2 } from './info-cell-v2';
import { CellBadge } from './cell-badge';
import { ContentBadge } from './content-badge';
import { selectEntryParentPreferenceEditorOptions } from '~/hooks/preference-selector/helpers';
import { usePreferenceSelector } from '~/hooks/preference-selector/use-preference-selector';
import {
  useFavoriteAction,
  type UseFavoriteActionReturnType,
} from '~/hooks/use-favorite-action';
import type { Action } from '~/hooks/actions/types';
import type { StorageFeedsData } from '~/components/ui-component';
import type { UseIsLoggedInReturnType } from '~/hooks/use-is-logged-in';
import {
  type AllDynamicColorsReturnType,
  getAllDynamicColors,
} from './get-cell-dynamic-colors';
import {
  usePopupMenuAvailability,
  type PopupMenuActionsSource,
  type PopupMenuAvailability,
} from '~/services/plugins/popup-menu/components/hooks/use-popup-menu-availability';
import {
  dispatchHidePopupMenu,
  dispatchShowPopupMenu,
  usePopupMenuFavoriteSource,
  type PopupMenuFavoriteSource,
  type PopupMenuTrigger,
  type PopupMenuTriggerHandler,
} from '~/services/plugins/popup-menu/components/host/index.ts';

export function PowerCellComponent({
  uiComponent,
  entry,
  selectable,
  cellClassNames,
  cellRef,
  feedRootWebLink,
  isLoading,
  storageFeedsData,
  isLoggedInData,
}: {
  uiComponent: UIComponent;
  entry: FeedEntry;
  selectable?: boolean;
  cellClassNames?: string;
  cellRef?: any;
  feedRootWebLink?: string;
  isLoading?: boolean;
  storageFeedsData?: StorageFeedsData;
  isLoggedInData?: UseIsLoggedInReturnType | undefined;
}): JSX.Element {
  const { cellStyles, timeZoneHeader, linkPrefetchSwitch } =
    useLoaderData<typeof rootLoader>();

  const progress: number | undefined = getContinueWatchingProgress({
    entry,
    continueWatchingFeed: storageFeedsData?.['continue-watching']?.feed,
  });

  isLoading = useMemo(
    () => isLoading || entry?._isLoadingSkeletonEntry,
    [entry?._isLoadingSkeletonEntry, isLoading]
  );

  const tapCellAnalyticsData: TapCellAnalyticsData = useTapCellAnalyticsData({
    uiComponent,
    entry,
  });

  const trackAnalyticsData: Function =
    useTrackAnalyticsData(tapCellAnalyticsData);

  const responsiveBreakpoint: UseResponsiveBreakpoint =
    useResponsiveBreakpoint();

  const {
    isPreferenceSelectorCellAndUnselected,
    isPreferenceSelectorCellAndSelected,
    preferenceSelectorActionFunction,
  } = usePreferenceSelector(entry, uiComponent);

  const favoritesFeed: Feed | undefined = storageFeedsData?.favorites?.feed;

  const favoriteActionData: UseFavoriteActionReturnType = useFavoriteAction({
    entry,
    favoritesFeed,
    isLoggedInData,
  });

  const tapActions = entry?.extensions?.tap_actions?.actions;

  const hasTapActions: boolean = useMemo(
    () => !isEmptyArray(tapActions as Action[]),
    [tapActions]
  );

  const isMultiSelectPreferenceEditor: boolean =
    selectEntryParentPreferenceEditorOptions(entry)?.select_mode === 'multi';

  const handleClick = useCallback(() => {
    if (
      isPreferenceSelectorCellAndSelected &&
      !hasTapActions &&
      !isMultiSelectPreferenceEditor
    ) {
      return;
    }
    preferenceSelectorActionFunction();
  }, [
    hasTapActions,
    isMultiSelectPreferenceEditor,
    isPreferenceSelectorCellAndSelected,
    preferenceSelectorActionFunction,
  ]);

  const preferTapActions: boolean =
    hasTapActions && entry?._role !== 'preference_editor';

  const entryWebLink: string = useMemo(
    () =>
      isPreferenceSelectorCellAndUnselected || preferTapActions
        ? ''
        : entry?._webLink || '',
    [entry?._webLink, isPreferenceSelectorCellAndUnselected, preferTapActions]
  );

  const isPowerCellHover: boolean = useMemo(
    () =>
      !!(
        entryWebLink ||
        isPreferenceSelectorCellAndUnselected ||
        isPreferenceSelectorCellAndSelected
      ),
    [
      entryWebLink,
      isPreferenceSelectorCellAndUnselected,
      isPreferenceSelectorCellAndSelected,
    ]
  );

  const {
    identifiers,
    styles,
    assets,
    cellClasses,
    textLabelClasses,
    textLabelsAlignment,
    pointerEventsClasses,
    powerCellClasses,
    cellContainerCellTypeClasses,
    cellImageContainerClasses,
    cellImageClasses,
    cellLabelContainerClasses,
  } = usePowerCellConfig({
    cellStyles,
    uiComponent,
    isPreferenceSelectorCellAndUnselected,
    isPreferenceSelectorCellAndSelected,
    isPowerCellHover,
    cellClassNames,
  });

  const defaultStyles = styles[seoSelectedBreakpoint];

  const actionIdentifierByBreakpoint = useMemo(() => {
    return getCellBreakpointValues({
      config: assets,
      key: 'action_identifier',
    });
  }, [assets]);

  const actionIdentifier = useMemo(() => {
    if (responsiveBreakpoint === 'unset') {
      return undefined;
    }

    const currentValue = actionIdentifierByBreakpoint[responsiveBreakpoint];

    return typeof currentValue === 'string' ? currentValue : undefined;
  }, [actionIdentifierByBreakpoint, responsiveBreakpoint]);

  const popupMenuAvailability: PopupMenuAvailability = usePopupMenuAvailability(
    {
      entry,
      actionIdentifier,
      favoritesAvailable: favoriteActionData.shouldRenderFavoriteAction,
      responsiveBreakpoint,
    }
  );

  // TODO: drop once FavoriteController lands on main — the host then reads
  // the entry's favourite state from the controller by entry id.
  const popupMenuFavoriteSource: PopupMenuFavoriteSource =
    usePopupMenuFavoriteSource(favoriteActionData);

  const popupMenuOwner: string = `power-cell-${useId()}`;

  useEffect(() => {
    return (): void => {
      dispatchHidePopupMenu({ owner: popupMenuOwner });
    };
  }, [popupMenuOwner]);

  const showCellPopupMenu = useCallback(
    ({
      actionsSource,
      ...trigger
    }: PopupMenuTrigger & { actionsSource: PopupMenuActionsSource }): void => {
      dispatchShowPopupMenu({
        ...trigger,
        owner: popupMenuOwner,
        uiComponent,
        content: {
          kind: 'entry',
          entry,
          actionsSource,
          favorite: popupMenuFavoriteSource,
        },
      });
    },
    [entry, popupMenuFavoriteSource, popupMenuOwner, uiComponent]
  );

  const handleConfiguredPopupMenuTrigger: PopupMenuTriggerHandler = useCallback(
    (trigger: PopupMenuTrigger): void => {
      showCellPopupMenu({ ...trigger, actionsSource: 'configured' });
    },
    [showCellPopupMenu]
  );

  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      if (!popupMenuAvailability.canOpenFromContextMenu) {
        return;
      }

      event.preventDefault();

      showCellPopupMenu({
        triggerElement: event.currentTarget,
        source: 'contextmenu',
        pointer: { x: event.clientX, y: event.clientY },
        actionsSource: popupMenuAvailability.contextMenuActionsSource,
      });
    },
    [popupMenuAvailability, showCellPopupMenu]
  );

  const buttonsPopupMenuTrigger: PopupMenuTriggerHandler | undefined =
    popupMenuAvailability.canOpenFromButtons
      ? handleConfiguredPopupMenuTrigger
      : undefined;

  const hasPointerEventsNoneClass = pointerEventsClasses.includes(
    'pointer-events-none'
  );

  const shouldForcePointerEventsForContextMenu =
    popupMenuAvailability.canOpenFromContextMenu && hasPointerEventsNoneClass;

  const effectivePointerEventsClasses = useMemo(() => {
    if (!shouldForcePointerEventsForContextMenu) {
      return pointerEventsClasses;
    }

    return pointerEventsClasses.replaceAll(
      'pointer-events-none',
      'pointer-events-auto'
    );
  }, [pointerEventsClasses, shouldForcePointerEventsForContextMenu]);

  const shouldDisableCellLink = shouldForcePointerEventsForContextMenu;

  const handleRootClickCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!shouldForcePointerEventsForContextMenu) {
        return;
      }

      const target = event.target as HTMLElement | null;

      if (!target) {
        return;
      }

      const isButtonsContainerActionClick = Boolean(
        target.closest('[data-power-cell-action-button="true"]')
      );
      const isActionIconClick = Boolean(
        target.closest('[data-testid^="action-icon-V2-"]')
      );

      if (isButtonsContainerActionClick || isActionIconClick) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
    },
    [shouldForcePointerEventsForContextMenu]
  );

  const textLabels: CellTextLabelsV2 = useMemo(
    () =>
      getCellTextLabelsV2({
        entry,
        styles,
        identifiers,
        timeZoneHeader,
        isLoading,
      }),
    [entry, identifiers, isLoading, styles, timeZoneHeader]
  );

  const overlayImageSources: ResponsiveSources = useMemo(
    () =>
      getCellBreakpointValues({
        config: assets,
        key: 'image_overlay',
        prevBreakpointFallback: false,
      }),
    [assets]
  );

  const cellImageSources: ResponsiveSources = useMemo(
    () =>
      getCellEntryImgSources({
        assets,
        entry,
      }),
    [assets, entry]
  );

  const dynamicColors = useMemo(
    () =>
      getAllDynamicColors({
        uiComponent,
        entry,
        cellStyles,
      }),
    [uiComponent, entry, cellStyles]
  ) as AllDynamicColorsReturnType;

  const LabelWrapperDynamicColors = useMemo(() => {
    return {
      textLabel: dynamicColors.textLabel,
      buttonsContainer: dynamicColors.buttonsContainer,
    };
  }, [dynamicColors.textLabel, dynamicColors.buttonsContainer]);

  try {
    return (
      <div
        className={twMerge(effectivePointerEventsClasses, 'h-full')}
        data-testid={`power-cell-${entry?.id}-${uiComponent?.id}`}
        onClick={handleClick}
        onClickCapture={handleRootClickCapture}
        onContextMenu={handleContextMenu}
      >
        <div
          ref={cellRef}
          className={powerCellClasses}
          css={dynamicColors.cell}
        >
          <MaybeLink
            to={entryWebLink}
            enabled={selectable && !shouldDisableCellLink}
            linkariainfo={entry?.title}
            analyticsdata={tapCellAnalyticsData}
            linkPrefetchSwitch={linkPrefetchSwitch}
          >
            <PowerCellBorder
              isPowerCellBorderHover={isPowerCellHover}
              isPowerCellBorderSelected={isPreferenceSelectorCellAndSelected}
              dynamicColors={dynamicColors.cellBorder}
            />

            <InfoCellV2
              identifiers={identifiers}
              assets={assets}
              styles={styles}
              textLabels={textLabels}
              cellClasses={cellClasses}
              feedRootWebLink={feedRootWebLink}
              trackAnalyticsData={trackAnalyticsData}
              defaultStyles={defaultStyles}
              entry={entry}
              isLoading={isLoading}
              dynamicColors={dynamicColors.infoCell}
            />
            <div className={cellContainerCellTypeClasses}>
              <div
                className={`relative rounded-image-border-radius bg-transparent ${cellImageContainerClasses}`}
              >
                <LockBadge entry={entry} assets={assets} />

                <RuntimeV2
                  entry={entry}
                  styles={styles}
                  dynamicColors={dynamicColors.runtime}
                />

                {!isLoading && popupMenuAvailability.shouldRenderActionIcon && (
                  <ActionIconV2
                    assets={assets}
                    entry={entry}
                    favoriteActionData={favoriteActionData}
                    actionIdentifier={actionIdentifier}
                    onPopupMenuTrigger={
                      popupMenuAvailability.canOpenFromActionIcon
                        ? handleConfiguredPopupMenuTrigger
                        : undefined
                    }
                  />
                )}

                <ContentBadge entry={entry} assets={assets} />

                {shouldRenderPowerCellImage({ assets, identifiers }) && (
                  <div className={cellImageClasses}>
                    <ImageOverlay
                      imageSources={overlayImageSources}
                      altText={entry?.title && `${entry?.title} overlay`}
                    />
                    <CellImage
                      entry={entry}
                      cellClasses={cellClasses}
                      isPowerCellImageHover={isPowerCellHover}
                      isPowerCellImageSelected={
                        isPreferenceSelectorCellAndSelected
                      }
                      assets={assets}
                      identifiers={identifiers}
                      imageSources={cellImageSources}
                      isLoading={isLoading}
                      dynamicColors={dynamicColors}
                    />
                  </div>
                )}

                <ProgressBarV2
                  assets={assets}
                  progress={progress}
                  dynamicColors={dynamicColors.progressBar}
                />
              </div>

              {/* Cell 1 and 2 Labels */}
              <div className="z-[6] flex flex-1 flex-col justify-center break-words">
                <LabelWrapper
                  textLabelNames={[
                    'text_label_1',
                    'text_label_2',
                    'text_label_3',
                    'text_label_4',
                  ]}
                  assets={assets}
                  textLabels={textLabels}
                  textLabelsAlignment={textLabelsAlignment}
                  textLabelClasses={textLabelClasses}
                  defaultStyles={defaultStyles}
                  entryWebLink={entryWebLink}
                  trackAnalyticsData={trackAnalyticsData}
                  identifiers={identifiers}
                  responsiveBreakpoint={responsiveBreakpoint}
                  entry={entry}
                  isLoading={isLoading}
                  isLabelHover={isPowerCellHover}
                  isPreferenceSelectorCellAndSelected={
                    isPreferenceSelectorCellAndSelected
                  }
                  favoriteActionData={favoriteActionData}
                  dynamicColors={LabelWrapperDynamicColors}
                  onPopupMenuTrigger={buttonsPopupMenuTrigger}
                />
              </div>
              {/* End Cell 1 and 2 Labels */}

              <CellBadge
                assets={assets}
                isHoverBadge={isPowerCellHover}
                isPreferenceSelectorCellAndSelected={
                  isPreferenceSelectorCellAndSelected
                }
              />

              {/* Cell 3 Labels */}
              <div className={cellLabelContainerClasses.top}>
                <LabelWrapper
                  textLabelNames={[
                    'top_text_label_1',
                    'top_text_label_2',
                    'top_text_label_3',
                  ]}
                  assets={assets}
                  textLabels={textLabels}
                  textLabelsAlignment={textLabelsAlignment}
                  textLabelClasses={textLabelClasses}
                  defaultStyles={defaultStyles}
                  entryWebLink={entryWebLink}
                  trackAnalyticsData={trackAnalyticsData}
                  identifiers={identifiers}
                  responsiveBreakpoint={responsiveBreakpoint}
                  entry={entry}
                  isLoading={false}
                  isLabelHover={isPowerCellHover}
                  favoriteActionData={favoriteActionData}
                  dynamicColors={LabelWrapperDynamicColors}
                  onPopupMenuTrigger={buttonsPopupMenuTrigger}
                />
              </div>

              <div className={cellLabelContainerClasses.center}>
                <LabelWrapper
                  textLabelNames={[
                    'center_text_label_1',
                    'center_text_label_2',
                    'center_text_label_3',
                  ]}
                  assets={assets}
                  textLabels={textLabels}
                  textLabelsAlignment={textLabelsAlignment}
                  textLabelClasses={textLabelClasses}
                  defaultStyles={defaultStyles}
                  entryWebLink={entryWebLink}
                  trackAnalyticsData={trackAnalyticsData}
                  identifiers={identifiers}
                  responsiveBreakpoint={responsiveBreakpoint}
                  entry={entry}
                  isLoading={false}
                  isLabelHover={isPowerCellHover}
                  favoriteActionData={favoriteActionData}
                  dynamicColors={LabelWrapperDynamicColors}
                  onPopupMenuTrigger={buttonsPopupMenuTrigger}
                />
              </div>

              <div className={cellLabelContainerClasses.bottom}>
                <LabelWrapper
                  textLabelNames={[
                    'bottom_text_label_1',
                    'bottom_text_label_2',
                    'bottom_text_label_3',
                  ]}
                  assets={assets}
                  textLabels={textLabels}
                  textLabelsAlignment={textLabelsAlignment}
                  textLabelClasses={textLabelClasses}
                  defaultStyles={defaultStyles}
                  entryWebLink={entryWebLink}
                  trackAnalyticsData={trackAnalyticsData}
                  identifiers={identifiers}
                  responsiveBreakpoint={responsiveBreakpoint}
                  entry={entry}
                  isLoading={false}
                  isLabelHover={isPowerCellHover}
                  favoriteActionData={favoriteActionData}
                  dynamicColors={LabelWrapperDynamicColors}
                  onPopupMenuTrigger={buttonsPopupMenuTrigger}
                />
              </div>
              {/* End Cell 3 Labels */}
            </div>
          </MaybeLink>
        </div>
      </div>
    );
  } catch (error: any) {
    logger.info(`PowerCell: ${error.message}`);
    return <></>;
  }
}

const usePowerCellConfig = ({
  cellStyles,
  uiComponent,
  isPreferenceSelectorCellAndUnselected,
  isPreferenceSelectorCellAndSelected,
  isPowerCellHover,
  cellClassNames,
}: any) => {
  const { identifiers, styles, assets } = useMemo(
    () =>
      getResponsiveCellConfigs({
        cellStyles,
        uiComponent,
      }),
    [cellStyles, uiComponent]
  );

  const cellClasses: string = useMemo(
    () => (uiComponent ? getCellClasses(uiComponent) : ''),
    [uiComponent]
  );

  const textLabelClasses: Record<string, string> = useMemo(
    () => getCellTextLabelClasses(cellClasses),
    [cellClasses]
  );

  const textLabelsAlignment = useMemo(
    () =>
      getResponsiveCellValues({
        cellKeys: cellTextLabels,
        cellKeysNameSuffix: 'text_alignment',
        config: styles,
        identifiers,
      }),
    [identifiers, styles]
  );

  const pointerEventsClasses: string = useMemo(
    () =>
      getPointerEventsClasses({
        assets,
        identifiers,
        isPreferenceSelectorCellAndUnselected,
        isPreferenceSelectorCellAndSelected,
      }),
    [
      assets,
      identifiers,
      isPreferenceSelectorCellAndUnselected,
      isPreferenceSelectorCellAndSelected,
    ]
  );

  const powerCellClasses: string = useMemo(
    () =>
      twMerge(
        cellClasses,
        'h-full',
        'z-0',
        'relative shrink-0 overflow-hidden rounded-cell-border-radius bg-cell pb-cell-b pl-cell-l pr-cell-r pt-cell-t',
        isPowerCellHover && 'hover:bg-cell-hover',
        isPreferenceSelectorCellAndSelected &&
          'bg-cell-selected hover:bg-cell-selected-hover',
        'rounded-cell-radius',
        'group',
        'custom-group',

        'cell-1-mobile',
        'cell-1-tablet',
        'cell-1-desktop',
        'cell-1-large-desktop',

        'cell-2-mobile',
        'cell-2-tablet',
        'cell-2-desktop',
        'cell-2-large-desktop',

        'cell-3-mobile',
        'cell-3-tablet',
        'cell-3-desktop',
        'cell-3-large-desktop',

        'cell-info-mobile',
        'cell-info-tablet',
        'cell-info-desktop',
        'cell-info-large-desktop',

        cellClassNames
      ),
    [
      cellClasses,
      isPowerCellHover,
      isPreferenceSelectorCellAndSelected,
      cellClassNames,
    ]
  );

  const cellContainerCellTypeClasses: string = useMemo(
    () =>
      getCellContainerCellTypeClasses({
        identifiers,
      }),
    [identifiers]
  );

  const cellImageContainerClasses: string = useMemo(
    () =>
      getCellImageContainerClasses({
        identifiers,
        assets,
      }),
    [assets, identifiers]
  );

  const cellImageClasses: string = useMemo(
    () =>
      getCellImageHiddenClasses({
        identifiers,
        assets,
      }),
    [assets, identifiers]
  );

  const cellLabelContainerClasses = useMemo(
    () => ({
      top: twMerge(
        getCellHiddenClasses({
          identifiers,
          identifier: 'cell_3_v2',
          classNames: 'absolute left-0 top-0 z-[6] w-full',
        }),
        getCell3IgnorePaddingClasses(identifiers, 'top')
      ),
      center: getCell3TextLabelContainerClasses({
        identifiers,
        classNames:
          'absolute left-1/2 top-1/2 z-[6] w-full -translate-x-1/2 -translate-y-1/2 transform',
      }),
      bottom: twMerge(
        getCell3TextLabelContainerClasses({
          identifiers,
          classNames: 'absolute bottom-0 left-0 z-[6] w-full',
        }),
        getCell3IgnorePaddingClasses(identifiers, 'bottom')
      ),
    }),
    [identifiers]
  );

  return {
    identifiers,
    styles,
    assets,
    cellClasses,
    textLabelClasses,
    textLabelsAlignment,
    pointerEventsClasses,
    powerCellClasses,
    cellContainerCellTypeClasses,
    cellImageContainerClasses,
    cellImageClasses,
    cellLabelContainerClasses,
  };
};

function CellImage({
  entry,
  cellClasses,
  isPowerCellImageHover,
  isPowerCellImageSelected,
  assets,
  identifiers,
  imageSources,
  isLoading,
  dynamicColors,
}: {
  entry: any;
  cellClasses: any;
  isPowerCellImageHover: boolean;
  isPowerCellImageSelected: boolean;
  assets: any;
  identifiers: any;
  imageSources: any;
  isLoading?: boolean;
  dynamicColors: AllDynamicColorsReturnType;
}) {
  if (isLoading) {
    return (
      <div
        className={twMerge(
          getCellImageAspectRatioClasses({ assets, identifiers }),
          'animate-pulse rounded-image-radius bg-skeleton_background_color',

          'relative rounded-image-radius',
          identifiers.mobile === 'cell_2_v2' &&
            'mobile:max-tablet:group-[.cell-2-mobile]:w-image-width',
          identifiers.tablet === 'cell_2_v2' &&
            'tablet:max-desktop:group-[.cell-2-tablet]:w-image-width',
          identifiers.desktop === 'cell_2_v2' &&
            'desktop:max-large-desktop:group-[.cell-2-desktop]:w-image-width',
          identifiers['large-desktop'] === 'cell_2_v2' &&
            'large-desktop:group-[.cell-2-large-desktop]:w-image-width',

          identifiers.mobile === 'cell_2_v2' &&
            'mobile:max-tablet:group-[.cell-2-mobile]:flex',
          identifiers.tablet === 'cell_2_v2' &&
            'tablet:max-desktop:group-[.cell-2-tablet]:flex',
          identifiers.desktop === 'cell_2_v2' &&
            'desktop:max-large-desktop:group-[.cell-2-desktop]:flex',
          identifiers['large-desktop'] === 'cell_2_v2' &&
            'large-desktop:group-[.cell-2-large-desktop]:flex'
        )}
        css={dynamicColors.cellImage.placeholder}
      />
    );
  }

  return (
    <div
      data-payload-image
      className={twMerge(
        getCellImagePlaceholderColorClasses({
          assets,
          identifiers,
          imageSources,
        }),
        getCellImageAspectRatioClasses({ assets, identifiers }),
        'relative rounded-image-radius',
        identifiers.mobile === 'cell_2_v2' &&
          'mobile:max-tablet:group-[.cell-2-mobile]:w-image-width',
        identifiers.tablet === 'cell_2_v2' &&
          'tablet:max-desktop:group-[.cell-2-tablet]:w-image-width',
        identifiers.desktop === 'cell_2_v2' &&
          'desktop:max-large-desktop:group-[.cell-2-desktop]:w-image-width',
        identifiers['large-desktop'] === 'cell_2_v2' &&
          'large-desktop:group-[.cell-2-large-desktop]:w-image-width',

        identifiers.mobile === 'cell_2_v2' &&
          'mobile:max-tablet:group-[.cell-2-mobile]:flex',
        identifiers.tablet === 'cell_2_v2' &&
          'tablet:max-desktop:group-[.cell-2-tablet]:flex',
        identifiers.desktop === 'cell_2_v2' &&
          'desktop:max-large-desktop:group-[.cell-2-desktop]:flex',
        identifiers['large-desktop'] === 'cell_2_v2' &&
          'large-desktop:group-[.cell-2-large-desktop]:flex'
      )}
    >
      <div
        className={twMerge(
          identifiers.mobile === 'cell_2_v2' &&
            'mobile:max-tablet:group-[.cell-2-mobile]:flex-shrink-0',
          identifiers.tablet === 'cell_2_v2' &&
            'tablet:max-desktop:group-[.cell-2-tablet]:flex-shrink-0',
          identifiers.desktop === 'cell_2_v2' &&
            'desktop:max-large-desktop:group-[.cell-2-desktop]:flex-shrink-0',
          identifiers['large-desktop'] === 'cell_2_v2' &&
            'large-desktop:group-[.cell-2-large-desktop]:flex-shrink-0'
        )}
      >
        <ResponsiveImage
          imageSources={imageSources}
          altText={entry?.title}
          classNames={twMerge(
            getCellImageCellTypeClasses({
              identifiers,
              cellClasses,
            }),
            'bg-image-placeholder'
          )}
          powerCellImage={{
            isPowerCellImageHover,
            isPowerCellImageSelected,
          }}
          assets={assets}
          identifiers={identifiers}
          dynamicColors={dynamicColors.cellImage}
        />
      </div>
    </div>
  );
}

export const PowerCell = React.memo(PowerCellComponent);
